/**
 * RFC 9457 ProblemDetails, surfaced rather than swallowed.
 *
 * The reference is explicit that a contradictory date filter is a 400 with a usable message,
 * not an empty list - so a client that catches errors and renders "no results" turns a fixable
 * mistake into a mystery. Every failure here carries a message a person can act on.
 */

/**
 * Fig returns TWO error shapes, and a client that knows only one throws away good messages.
 *
 *  - **RFC 9457 ProblemDetails** (`title`, `detail`, `status`) for anything the framework
 *    produces: model binding, validation, unhandled exceptions.
 *  - **`{ "error": "..." }`** for business refusals written by hand in a controller - "This
 *    account is set up to manage its own business directly.", "This person has already
 *    accepted. Disable them instead of deleting them.", and every other 409.
 *
 * The API reference documents only the first, which is how this client came to read `detail`
 * and `title` and nothing else - and then invented a generic message for a 409 that had been
 * carrying a perfectly good one all along. Confirmed against the live API, 2026-09-06.
 */
export interface ProblemDetails {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly instance?: string;
  /** The hand-written business-error shape. */
  readonly error?: string;
  readonly [key: string]: unknown;
}

export class FigApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  /** The raw body when it was not ProblemDetails - useful in a bug report, never shown. */
  readonly body: string | null;

  constructor(status: number, problem: ProblemDetails | null, body: string | null) {
    super(FigApiError.humanMessage(status, problem));
    this.name = 'FigApiError';
    this.status = status;
    this.problem = problem;
    this.body = body;
  }

  /** True when re-exchanging the Clerk token could plausibly fix this. */
  get isExpiredSession(): boolean { return this.status === 401; }
  /** True when the caller is authenticated but not allowed - re-exchanging will not help. */
  get isForbidden(): boolean { return this.status === 403; }
  get isNotFound(): boolean { return this.status === 404; }
  get isConflict(): boolean { return this.status === 409; }
  get isRateLimited(): boolean { return this.status === 429; }
  get isServerFault(): boolean { return this.status >= 500; }
  /** Retrying the identical request is reasonable. */
  get isTransient(): boolean { return this.status === 429 || this.status >= 500; }

  /**
   * A message for a person, in their words.
   *
   * The server's own `detail` wins when it exists, because it is specific ("toDate is before
   * fromDate") in a way a generic string never is. The fallbacks below only run when the
   * server said nothing useful, and they deliberately do not name HTTP - "403" is not English.
   */
  static humanMessage(status: number, problem: ProblemDetails | null): string {
    // `error` first: it is the hand-written, situation-specific one. `detail` and `title`
    // come from the framework and are more often generic.
    const detail = problem?.error?.trim() || problem?.detail?.trim() || problem?.title?.trim();
    if (detail) return detail;
    switch (status) {
      case 0:   return 'Fig could not reach the network. Check your connection and try again.';
      case 400: return 'That request could not be understood. Try adjusting the filters.';
      case 401: return 'Your session has ended. Sign in again to continue.';
      case 403: return 'You do not have access to that.';
      case 404: return 'That could not be found.';
      // A 409 without a server message is genuinely ambiguous - a stale write, a duplicate,
      // or a state that forbids the action (creating a client in Direct mode returns one).
      // Inventing a cause would be a small lie, so this names none. The real fix is on the
      // API: a 409 with no `detail` cannot be explained to anybody. Logged as a gap.
      case 409: return 'Fig could not complete that. Reload and try again.';
      case 429: return 'Too many requests just now. Wait a moment and try again.';
      case 503: return 'Fig is temporarily unavailable. Try again shortly.';
      default:
        return status >= 500
          ? 'Something went wrong at our end. Try again shortly.'
          : 'That did not work. Try again.';
    }
  }
}

/** A conversation with no recording. Normal for text-only channels - never an error state. */
export class NoRecordingError extends Error {
  readonly conversationId: string;
  constructor(conversationId: string) {
    super('This conversation has no recording.');
    this.name = 'NoRecordingError';
    this.conversationId = conversationId;
  }
}

export function parseProblem(text: string): ProblemDetails | null {
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === 'object' ? (v as ProblemDetails) : null;
  } catch { return null; }
}
